/**
 * Greybox path / bridge mesh — long deck along +Z with painted center lane,
 * concrete guardrails + periodic posts, and faint city skyline silhouettes
 * at the horizon edges. Pure presentation; layout from pathLength + halfWidth.
 */
import * as THREE from "three";
import { makeGroundMaterial, makePathDeckMaterial, makeGuardrailMaterial, makeLaneLineMaterial, makeSkylineMaterial, } from "./materials.js";
import { DEFAULT_PATH_HALF_WIDTH } from "./snapshot.js";
const DECK_THICKNESS = 0.35;
/** Concrete guardrail beam cross-section. */
const GUARDRAIL_HEIGHT = 0.72;
const GUARDRAIL_THICKNESS = 0.28;
/** Periodic upright posts along each guardrail. */
const GUARDRAIL_POST_W = 0.32;
const GUARDRAIL_POST_H = 1.05;
const GUARDRAIL_POST_D = 0.32;
const GUARDRAIL_POST_SPACING = 4.5;
/** Painted center lane line. */
const LANE_LINE_WIDTH = 0.22;
const LANE_LINE_Y = 0.02;
const LANE_DASH_LEN = 2.4;
const LANE_GAP_LEN = 1.6;
const GROUND_PAD = 40;
/** Skyline boxes sit past the deck end, partially in fog. */
const SKYLINE_DEPTH = 18;
const SKYLINE_BASE_H = 6;
/**
 * Build a bridge segment from z=0 to z=pathLength, centered on X=0.
 * Deck top sits at y=0 so unit feet rest on the surface.
 */
export function createPathBridge(layout) {
    const halfWidth = layout.halfWidth > 0 ? layout.halfWidth : DEFAULT_PATH_HALF_WIDTH;
    const length = Math.max(1, layout.pathLength);
    const width = halfWidth * 2;
    const group = new THREE.Group();
    group.name = "pathBridge";
    const deckMat = makePathDeckMaterial();
    const guardrailMat = makeGuardrailMaterial();
    const laneMat = makeLaneLineMaterial();
    const skylineMat = makeSkylineMaterial();
    const groundMat = makeGroundMaterial();
    // Deck: top face at y=0
    const deckGeo = new THREE.BoxGeometry(width, DECK_THICKNESS, length);
    const deck = new THREE.Mesh(deckGeo, deckMat);
    deck.name = "pathDeck";
    deck.position.set(0, -DECK_THICKNESS / 2, length / 2);
    deck.receiveShadow = true;
    group.add(deck);
    // --- Concrete guardrails (both edges) + periodic posts -------------------
    const railY = GUARDRAIL_HEIGHT / 2 - DECK_THICKNESS * 0.2;
    const leftX = -halfWidth + GUARDRAIL_THICKNESS / 2;
    const rightX = halfWidth - GUARDRAIL_THICKNESS / 2;
    const leftRailGeo = new THREE.BoxGeometry(GUARDRAIL_THICKNESS, GUARDRAIL_HEIGHT, length);
    const leftGuardrail = new THREE.Mesh(leftRailGeo, guardrailMat);
    leftGuardrail.name = "guardrailLeft";
    leftGuardrail.position.set(leftX, railY, length / 2);
    group.add(leftGuardrail);
    const rightRailGeo = new THREE.BoxGeometry(GUARDRAIL_THICKNESS, GUARDRAIL_HEIGHT, length);
    const rightGuardrail = new THREE.Mesh(rightRailGeo, guardrailMat);
    rightGuardrail.name = "guardrailRight";
    rightGuardrail.position.set(rightX, railY, length / 2);
    group.add(rightGuardrail);
    // Instanced posts along each guardrail (shared geometry, repeated)
    const postCount = Math.max(2, Math.floor(length / GUARDRAIL_POST_SPACING) + 1);
    const postGeo = new THREE.BoxGeometry(GUARDRAIL_POST_W, GUARDRAIL_POST_H, GUARDRAIL_POST_D);
    const postMat = guardrailMat.clone();
    const dummy = new THREE.Object3D();
    const postY = GUARDRAIL_POST_H / 2 - DECK_THICKNESS * 0.15;
    const leftPosts = new THREE.InstancedMesh(postGeo, postMat, postCount);
    leftPosts.name = "guardrailPostsLeft";
    leftPosts.count = postCount;
    const rightPosts = new THREE.InstancedMesh(postGeo, postMat, postCount);
    rightPosts.name = "guardrailPostsRight";
    rightPosts.count = postCount;
    for (let i = 0; i < postCount; i++) {
        const z = postCount === 1
            ? length / 2
            : (i / (postCount - 1)) * length;
        dummy.position.set(leftX, postY, z);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        leftPosts.setMatrixAt(i, dummy.matrix);
        dummy.position.set(rightX, postY, z);
        dummy.updateMatrix();
        rightPosts.setMatrixAt(i, dummy.matrix);
    }
    leftPosts.instanceMatrix.needsUpdate = true;
    rightPosts.instanceMatrix.needsUpdate = true;
    group.add(leftPosts);
    group.add(rightPosts);
    // --- Painted center lane line (dashed dashes down the deck) --------------
    const laneLine = new THREE.Group();
    laneLine.name = "centerLaneLine";
    const dashStride = LANE_DASH_LEN + LANE_GAP_LEN;
    const dashCount = Math.max(1, Math.floor(length / dashStride));
    const dashGeo = new THREE.BoxGeometry(LANE_LINE_WIDTH, 0.04, LANE_DASH_LEN);
    const dashMeshes = [];
    for (let i = 0; i < dashCount; i++) {
        const z = LANE_DASH_LEN / 2 + i * dashStride;
        if (z + LANE_DASH_LEN / 2 > length)
            break;
        const dash = new THREE.Mesh(dashGeo, laneMat);
        dash.name = `laneLineDash_${i}`;
        dash.position.set(0, LANE_LINE_Y, z);
        laneLine.add(dash);
        dashMeshes.push(dash);
    }
    group.add(laneLine);
    // --- Faint city skyline silhouettes at the horizon (fog edges) -----------
    const skyline = createSkylineGroup(length, halfWidth, skylineMat);
    group.add(skyline);
    // Distant ground plane under the bridge (greybox void)
    const groundGeo = new THREE.PlaneGeometry(width + GROUND_PAD * 2, length + GROUND_PAD * 2 + SKYLINE_DEPTH * 2);
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.name = "ground";
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -4, length / 2 + SKYLINE_DEPTH * 0.25);
    ground.receiveShadow = true;
    group.add(ground);
    const dispose = () => {
        deckGeo.dispose();
        leftRailGeo.dispose();
        rightRailGeo.dispose();
        postGeo.dispose();
        dashGeo.dispose();
        groundGeo.dispose();
        deckMat.dispose();
        guardrailMat.dispose();
        postMat.dispose();
        laneMat.dispose();
        skylineMat.dispose();
        groundMat.dispose();
        // Skyline box geos
        skyline.traverse((obj) => {
            if (obj instanceof THREE.Mesh && obj.geometry) {
                obj.geometry.dispose();
            }
        });
        leftPosts.dispose();
        rightPosts.dispose();
        group.clear();
    };
    return {
        group,
        deck,
        leftGuardrail,
        rightGuardrail,
        leftPosts,
        rightPosts,
        laneLine,
        skyline,
        ground,
        dispose,
    };
}
/**
 * Low-poly building silhouettes left/right of the far deck, sitting in fog.
 * Flat vertex colors only — greybox+ tier.
 */
function createSkylineGroup(pathLength, halfWidth, mat) {
    const skyline = new THREE.Group();
    skyline.name = "citySkyline";
    // Building footprints: [width, height, depth, xSign, zBias, yScale]
    const buildings = [
        // left cluster
        [3.2, 14, 2.4, -1, 0],
        [2.4, 9, 2.0, -1, 4],
        [4.0, 18, 2.8, -1, 8],
        [2.0, 11, 1.8, -1, 12],
        [3.5, 7, 2.2, -1, 16],
        [2.8, 15, 2.0, -1, 20],
        // right cluster
        [2.6, 12, 2.2, 1, 1],
        [3.8, 16, 2.6, 1, 5],
        [2.2, 8, 1.9, 1, 9],
        [4.2, 20, 3.0, 1, 13],
        [2.5, 10, 2.1, 1, 17],
        [3.0, 13, 2.3, 1, 21],
        // far center stack (very faint, deeper in fog)
        [5.0, 22, 3.5, 0, 6],
        [3.5, 17, 2.5, 0, 14],
    ];
    const baseZ = pathLength + SKYLINE_DEPTH * 0.55;
    const sideInset = halfWidth + 6;
    for (let i = 0; i < buildings.length; i++) {
        const [bw, bh, bd, xSign, zBias] = buildings[i];
        const geo = new THREE.BoxGeometry(bw, bh, bd);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.name = `skylineBuilding_${i}`;
        const x = xSign === 0
            ? (i % 2 === 0 ? -2.5 : 2.8)
            : xSign * (sideInset + (i % 3) * 1.4);
        const z = baseZ + zBias * 0.55;
        mesh.position.set(x, bh / 2 - 0.5, z);
        skyline.add(mesh);
    }
    // Thin far plane under buildings so silhouette reads as a mass edge
    const planeGeo = new THREE.BoxGeometry(halfWidth * 2 + 48, SKYLINE_BASE_H * 0.35, 1.2);
    const plane = new THREE.Mesh(planeGeo, mat);
    plane.name = "skylineHorizonPlane";
    plane.position.set(0, SKYLINE_BASE_H * 0.12, baseZ + 4);
    skyline.add(plane);
    return skyline;
}
/** Rebuild bridge when path length / width changes. */
export function rebuildPathBridge(parent, previous, layout) {
    if (previous) {
        parent.remove(previous.group);
        previous.dispose();
    }
    const next = createPathBridge(layout);
    parent.add(next.group);
    return next;
}
//# sourceMappingURL=pathBridge.js.map